
import { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Toggle } from '@/components/ui/toggle';
import { EditUserDialog } from '@/components/users/edit-user-dialog';
import { DeleteUserDialog } from '@/components/users/delete-user-dialog';
import {
  MoreHorizontalIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  QrCode,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiRequest } from '@/lib/api-client';
import { useGym } from '@/contexts/gym-context';
import { useToast } from '@/hooks/use-toast';
import { LoginQRDialog } from '@/components/shared/login-qr-dialog';
import { useUsersPaginated } from '@/api/users';

interface User {
  _id: string;
  name: string;
  surname: string;
  email: string;
  phone: string;
  status: string;
  userType: string;
  gym: string[];
  createdAt: string;
  expires?: string;
  activePayment?: {
    status: string;
    _id: string;
  };
  isFixed?: boolean;
  remainingEntries?: number;
}

interface UserTableProps {
  searchQuery: string;
  showPartiallyPaidOnly: boolean;
}

export type UserTableRef = {
  fetchUsers: () => Promise<void>;
};

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export const UserTable = forwardRef<UserTableRef, UserTableProps>(
  ({ searchQuery = '', showPartiallyPaidOnly = false }, ref) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { selectedGym } = useGym();

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

    const debouncedSearch = useDebounce(searchQuery, 300);

    // Reset to first page when search or page size changes
    useEffect(() => {
      setCurrentPage(1);
    }, [debouncedSearch, showPartiallyPaidOnly, itemsPerPage]);

    const { data: pageData, isLoading, refetch } = useUsersPaginated({
      search: debouncedSearch,
      page: currentPage,
      limit: itemsPerPage,
      userType: 'user',
    });

    const users: User[] = (pageData?.results ?? []) as unknown as User[];
    const total = pageData?.total ?? 0;
    const totalPages = pageData?.totalPages ?? 1;

    // Apply client-side partially-paid filter on current page results
    const filteredUsers = showPartiallyPaidOnly
      ? users.filter((u) => u.activePayment?.status === 'partially_paid')
      : users;

    const [editingUser, setEditingUser] = useState<string | null>(null);
    const [deletingUser, setDeletingUser] = useState<string | null>(null);
    const [qrUser, setQrUser] = useState<{ id: string; name: string } | null>(null);
    const [optimisticStatuses, setOptimisticStatuses] = useState<Record<string, string>>({});

    const fetchUsers = useCallback(async () => {
      await refetch();
    }, [refetch]);

    useImperativeHandle(ref, () => ({ fetchUsers }));

    const handleStatusToggle = async (userId: string, currentStatus: string) => {
      setOptimisticStatuses((prev) => ({
        ...prev,
        [userId]: currentStatus === 'active' ? 'inactive' : 'active',
      }));
      try {
        await apiRequest({
          method: 'PUT',
          url: `/users/${userId}/toggle-status`,
        });
        toast({ title: 'Success', description: 'User status updated successfully' });
      } catch (error) {
        setOptimisticStatuses((prev) => ({ ...prev, [userId]: currentStatus }));
        console.error('Failed to toggle user status:', error);
        toast({
          title: 'Error',
          description: 'Failed to update user status',
          variant: 'destructive',
        });
      }
    };

    const formatDate = (dateString?: string) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString();
    };

    const getUserStatus = (user: User) => optimisticStatuses[user._id] ?? user.status;

    return (
      <>
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <p>{t('common.loading')}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('users.name')}</TableHead>
                <TableHead>{t('users.email')}</TableHead>
                <TableHead>{t('users.phone')}</TableHead>
                <TableHead>{t('users.status')}</TableHead>
                <TableHead>{t('users.joinDate')}</TableHead>
                <TableHead>{t('users.remaining')}</TableHead>
                <TableHead>{t('users.expirationDate')}</TableHead>
                <TableHead>{t('common.active')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    {t('users.noUsers')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => {
                  const status = getUserStatus(user);
                  return (
                    <TableRow key={user._id}>
                      <TableCell className="font-medium">
                        <a
                          href={`/users/detail?id=${user._id}`}
                          className="text-primary hover:text-primary/80 focus:outline-none focus:underline"
                          tabIndex={0}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/users/detail?id=${user._id}`);
                          }}
                        >
                          {user.name} {user.surname}
                        </a>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone}</TableCell>
                      <TableCell>
                        <Badge variant={status === 'active' ? 'default' : 'secondary'}>
                          {status === 'active' ? t('users.active') : t('users.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell>
                        {user?.activePayment
                          ? user?.isFixed
                            ? user?.remainingEntries + ' - total'
                            : user?.remainingEntries + ' - this month'
                          : 0}
                      </TableCell>
                      <TableCell>{formatDate(user.expires)}</TableCell>
                      <TableCell>
                        <Toggle
                          pressed={status === 'active'}
                          onPressedChange={() => handleStatusToggle(user._id, status)}
                          aria-label={`Toggle ${user.name} ${user.surname} status`}
                          size="sm"
                        >
                          {status === 'active' ? 'Yes' : 'No'}
                        </Toggle>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontalIcon className="h-4 w-4" />
                              <span className="sr-only">{t('common.actions')}</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/users/detail?id=${user._id}`)}
                            >
                              <EyeIcon className="h-4 w-4 mr-2" />
                              {t('users.view')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditingUser(user._id)}>
                              <PencilIcon className="h-4 w-4 mr-2" />
                              {t('common.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setDeletingUser(user._id)}>
                              <TrashIcon className="h-4 w-4 mr-2" />
                              {t('common.delete')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                setQrUser({
                                  id: user._id,
                                  name: `${user.name} ${user.surname}`.trim(),
                                })
                              }
                            >
                              <QrCode className="h-4 w-4 mr-2" />
                              Generate Login QR
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          {/* Left: total count + page-size selector */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {total} {t('users.totalUsers', { defaultValue: 'users' })}
            </span>
            <Select
              value={String(itemsPerPage)}
              onValueChange={(v) => setItemsPerPage(Number(v))}
            >
              <SelectTrigger className="h-8 w-[90px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Right: page navigation */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('common.previous')}
              </Button>

              {(() => {
                const pageGroupSize = 10;
                const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
                const groupStart = currentGroup * pageGroupSize + 1;
                const groupEnd = Math.min(groupStart + pageGroupSize - 1, totalPages);
                const buttons = [];

                if (groupStart > 1) {
                  buttons.push(
                    <Button
                      key="prev-group"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(groupStart - 1)}
                      className="w-8 h-8 p-0"
                    >
                      ...
                    </Button>
                  );
                }

                for (let p = groupStart; p <= groupEnd; p++) {
                  buttons.push(
                    <Button
                      key={p}
                      variant={currentPage === p ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(p)}
                      className="w-8 h-8 p-0"
                    >
                      {p}
                    </Button>
                  );
                }

                if (groupEnd < totalPages) {
                  buttons.push(
                    <Button
                      key="next-group"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(groupEnd + 1)}
                      className="w-8 h-8 p-0"
                    >
                      ...
                    </Button>
                  );
                }

                return buttons;
              })()}

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                {t('common.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {editingUser && (
          <EditUserDialog
            userId={editingUser}
            open={!!editingUser}
            onOpenChange={() => setEditingUser(null)}
            onSuccess={fetchUsers}
          />
        )}

        {deletingUser && (
          <DeleteUserDialog
            userId={deletingUser}
            open={!!deletingUser}
            onOpenChange={() => setDeletingUser(null)}
            onSuccess={fetchUsers}
          />
        )}

        {qrUser && (
          <LoginQRDialog
            userId={qrUser.id}
            userName={qrUser.name}
            open={!!qrUser}
            onOpenChange={(open) => {
              if (!open) setQrUser(null);
            }}
          />
        )}
      </>
    );
  }
);

UserTable.displayName = 'UserTable';
